import RawEditor from 'react-monaco-editor';
import React, { useEffect, useState } from 'react';
import { push as Push, replace as Replace } from 'redux-first-history';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty, reduce } from 'lodash-es';
import { Editor as EditorModule } from '@dev/ee-modules';
import { Dialog as MaterialDialog, Grid, Portal } from '@material-ui/core';
import { palette } from '@dev/ee-components';
import styled from 'styled-components';

import { useTranslation } from 'react-i18next';
import { ApplicationState, getConfigStore } from '@/store';
import { GetContactFieldsStart } from '@/store/customFields/actions';
import { SetToastPortalWrapper } from '@/store/toast/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import {
    ChangeName,
    ChangeTemplateBody,
    LoadTemplate,
    ModifyAMPBody,
    ModifyTextBody,
    RedoTemplateChanges,
    SavePastBody,
    UndoTemplateChanges,
    SaveScreenshot,
    getRawEditorState,
    CloseTemplateEditor,
    CloseEditorInCampaignCreator,
} from '@/store/raweditor/actions';
import { getDialogStore, HideDialog, ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { getCustomFieldsStore } from '@/store/customFields/actions';
import { StartUpdatingTemplateParams, templateUpdateStart } from '@/store/landingPageList/actions';
import { HideToast, ShowToast } from '@/store/toast/actions';
import {
    getMediaManagerStore,
    HideMediaManager,
    HideMediaManagerUploader,
    ShowFileUpload,
    ShowMediaManager,
    ShowMediaManagerUploader,
} from '@/store/mediaManager/actions';
import { DeleteFile, DeleteFilesList, getUserFilesStore, LoadUserFiles } from '@/store/userfiles/actions';
import { getUploadStore, UploadFilesMedia as UploadFiles } from '@/store/upload/actions';
import { FileType } from '@/store/mediaManager/types';
import { ShowToastParams } from '@/store/toast/types';
import { getAccountStore } from '@/store/account/actions';
import { useGetContentTemplateName } from '@/components/CampaignCreator/hooks/useGetContentTemplateName';
import { CloseGallery, ContinueWithHTMLTemplate } from '@/store/campaignCreator/actions';
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';

const Overlay = styled.div`
    background: ${palette.snow[1]};
    height: max-content;

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

export const CampaignRawEditor = () => {
    const dispatch = useDispatch();
    const customFields = useSelector(getCustomFieldsStore);
    const { t } = useTranslation();
    const config = useSelector(getConfigStore);
    const account = useSelector(getAccountStore);
    const browser = useSelector((state: ApplicationState) => state.browser);
    const rawEditorState = useSelector(getRawEditorState);
    const dialog = useSelector(getDialogStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const upload = useSelector(getUploadStore);
    const userFiles = useSelector(getUserFilesStore);
    const locationHistory = useSelector((state: ApplicationState) => state.locationHistory.currentLocation);
    const templateName = useGetContentTemplateName();
    const historyBackAction = useHistoryBackActionContext();

    const [isRawEditorOpen, setIsRawEditorOpen] = useState(rawEditorState.isCampaignCreator);

    useEffect(() => {
        if (!isRawEditorOpen) {
            return;
        }
        
        dispatch(GetContactFieldsStart({ isUrlUpdateDisabled: true }));
        dispatch(SetToastPortalWrapper(true));
        historyBackAction?.pushBackCallback(handleBacktToCampaignCreator) 
        
        return () => {
            dispatch(SetToastPortalWrapper(false));
        };
    }, [isRawEditorOpen]);

    useEffect(() => {
        setIsRawEditorOpen(rawEditorState.isCampaignCreator);
    }, [rawEditorState.isCampaignCreator]);

    const availableActions = {
        ChangeTemplateBody: (template: string) => dispatch(ChangeTemplateBody(template)),
        GetContactFieldsStart: () => dispatch(GetContactFieldsStart()),
        LoadTemplate: (name: string, isNew: boolean, isPublic?: boolean) =>
            dispatch(LoadTemplate(name, isNew, isPublic)),
        RedoTemplateChanges: (previewRef: React.Ref<HTMLIFrameElement>) => dispatch(RedoTemplateChanges(previewRef)),
        UndoTemplateChanges: (previewRef: React.Ref<HTMLIFrameElement>) => dispatch(UndoTemplateChanges(previewRef)),
        ChangeName: (name: string, previewRef: React.Ref<HTMLIFrameElement>) => dispatch(ChangeName(name, previewRef)),
        ShowDialog: (params: ShowDialogParams) => dispatch(ShowDialog(params)),
        ModifyAMPBody: (body: string, previewRef: React.Ref<HTMLIFrameElement>) =>
            dispatch(ModifyAMPBody(body, previewRef)),
        ModifyTextBody: (body: string, previewRef: React.Ref<HTMLIFrameElement>) =>
            dispatch(ModifyTextBody(body, previewRef)),
        SavePastBody: (template: string, previewRef: React.Ref<HTMLIFrameElement>) =>
            dispatch(SavePastBody(template, previewRef)),
        SaveScreenshot: (previewRef: React.Ref<HTMLIFrameElement>) => dispatch(SaveScreenshot(previewRef)),
        HideDialog: () => dispatch(HideDialog()),
        UpdateDialog: (params: ShowDialogParams) => dispatch(UpdateDialog(params)),
        Replace: (url: string) => dispatch(Replace(url)),
        Push: (url: string) => dispatch(Push(url)),
        ShowMediaManager: () => dispatch(ShowMediaManager()),
        HideMediaManager: () => dispatch(HideMediaManager()),
        UploadFiles: (file: File) => dispatch(UploadFiles(file)),
        LoadUserFiles: () => dispatch(LoadUserFiles()),
        DeleteFile: (name: string, imageDelete: boolean) => dispatch(DeleteFile(name, imageDelete)),
        DeleteFilesList: (filesList: string[], imagesDelete: boolean) =>
            dispatch(DeleteFilesList(filesList, imagesDelete)),
        ShowMediaManagerUploader: () => dispatch(ShowMediaManagerUploader()),
        HideMediaManagerUploader: () => dispatch(HideMediaManagerUploader()),
        ShowFileUpload: (file: FileType) => dispatch(ShowFileUpload(file)),
        ShowToast: (params: ShowToastParams) => dispatch(ShowToast(params)),
        templateUpdateStart: (params: StartUpdatingTemplateParams) => dispatch(templateUpdateStart(params)),
        CloseTemplateEditor: () => dispatch(CloseTemplateEditor()),
    };

    const monacoRawEditor = RawEditor;

    const reduceCustomFields = (): Record<string, string> => {
        if (isEmpty(customFields.collection)) {
            return {};
        }

        return reduce(
            customFields.collection,
            (customField, item) => ({
                ...customField,
                [`{${item.Name}}`]: item.Name,
            }),
            {}
        );
    };

    const handleBacktToCampaignCreator = (): boolean => {
        if (rawEditorState.changesSaved === false) {
            dispatch(
                ShowToast({
                    message: t('templateisbeingsaved'),
                    severity: 'info',
                    duration: 6000,
                    width: 380,
                    onClose: HideToast,
                })
            );
            return;
        }
        
        if (rawEditorState.isNewTemplate === false) {
            dispatch(ContinueWithHTMLTemplate());
        }
        dispatch(CloseEditorInCampaignCreator());
        dispatch(CloseGallery());
        dispatch(Replace({ search: null }));

        return true;
    };

    const onClose = (): void => {
        historyBackAction.popAndExecuteBackCallback();
    };

    const handleContinueWithTemplate = () => {
        setIsRawEditorOpen(false);
        dispatch(ContinueWithHTMLTemplate());
        dispatch(CloseEditorInCampaignCreator());
        dispatch(CloseGallery());
        dispatch(Replace({ search: null }));
    };

    return (
        <Portal>
            <MaterialDialog open={isRawEditorOpen} fullScreen={true} scroll={'body'}>
                <Overlay>
                    <Grid container justifyContent="center">
                        <Grid item xs={12} sm={12}>
                            <EditorModule
                                config={config}
                                account={account}
                                browser={browser}
                                rawEditor={rawEditorState}
                                dialog={dialog}
                                mediaManager={mediaManager}
                                upload={upload}
                                userFiles={userFiles}
                                location={locationHistory}
                                contactFields={reduceCustomFields()}
                                APIPATH={APIPATH}
                                // eslint-disable-next-line react/jsx-no-duplicate-props
                                RawEditor={monacoRawEditor}
                                isCampaignCreator
                                ContinueWithTemplate={handleContinueWithTemplate}
                                BackToCampaignCreator={onClose}
                                campaignTemplate={templateName}
                                {...availableActions}
                            />
                        </Grid>
                    </Grid>
                </Overlay>
            </MaterialDialog>
        </Portal>
    );
};
